import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    clearAppointmentSlotsSearch,
    searchAppointmentSlots
} from '../../../store/calendarService';
import {
    loadAppointmentTypeDetailsByContractIds,
    loadServiceDetailsByService
} from '../../../store/directusService';
import { loadTeamsForService } from '../../../store/userService';
import { calcEndDate, parseDateRange, todayDate } from '../../../utils/dateFunctions';
import { getObjFromId } from '../../../utils/directusFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateRangePicker from '../../formElements/DateRangePicker';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import StaticField from '../../formElements/StaticField';

import AppointmentSlotsTableManagement from './AppointmentSlotsTableManagement';
import { MAX_DATE_RANGE, validate } from './validateSlotsSearchFilters';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './appointmentSlotsSearch.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
export const INITIAL_DATE_RANGE = 7;

const AppointmentSlotsSearch = () => {
    const dispatch = useDispatch();
    let { state } = useLocation();
    if (!state)
        state = {
            contractId: '',
            serviceId: '',
            teamId: '',
            participantId: ''
        };

    // LOCAL STATE
    const initialFilters = {
        dateFrom: '',
        dateTo: '',
        serviceId: '',
        teamId: '',
        typeIds: [],
        includeFilled: false
    };
    const [initialDates, setInitialDates] = useState({ dateFrom: '', dateTo: '' });
    const [filters, setFilters] = useState(initialFilters);
    const [initialFilterState, setInitialFilterState] = useState(initialFilters);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const roles = loggedInUser.roles;
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const teamsForService = useSelector((state) => state.entities.userService.teamsForService);
    const appointmentTypes = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetailsForContracts
    );

    // USEEFFECTS
    useEffect(() => {
        state?.contractId && dispatch(loadAppointmentTypeDetailsByContractIds(state.contractId));
        state?.serviceId && dispatch(loadTeamsForService({ serviceIds: [state.serviceId] }));

        setInitialDates({
            dateFrom: todayDate(),
            dateTo: calcEndDate(todayDate(), INITIAL_DATE_RANGE)
        });

        return () => clearForm();
    }, []);

    useEffect(() => {
        if (!('serviceId' in state)) return;
        if (!(state.serviceId in serviceDetailsById))
            state.serviceId && dispatch(loadServiceDetailsByService([state.serviceId]));
        else
            setFilters({
                ...filters,
                serviceId: state.serviceId,
                teamId: state.teamId
            });
    }, [serviceDetailsById]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const clearForm = () => {
        setFilters(initialFilters);
        setInitialFilterState(initialFilters);
        setErrors({});
        setIsUpdateButtonDisabled(false);
        dispatch(clearAppointmentSlotsSearch());
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        if (isUpdateButtonDisabled) setIsUpdateButtonDisabled(false);
        setFilters((filters) => ({ ...filters, [key]: value }));
    };

    const onDateChange = (searchRange) => {
        clearError('dateRange');
        if (isUpdateButtonDisabled) setIsUpdateButtonDisabled(false);
        const { dateFrom, dateTo } = parseDateRange(searchRange);
        setFilters((filters) => ({ ...filters, dateFrom, dateTo }));
    };

    const onSearch = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(filters, initialFilterState);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(searchAppointmentSlots(filters, 0));
        setInitialFilterState(filters);
    };

    return (
        <div className={app.pageContainer}>
            <h2 className={app.mainHeading}>Appointment Slots</h2>
            <form onSubmit={onSearch} data-testid="appointment_slot_search_form_start">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateRangePicker
                            id="dateRange"
                            label="Appointment Slot Date Range"
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            initialStartDate={initialDates.dateFrom}
                            initialEndDate={initialDates.dateTo}
                            minStartDate={todayDate()}
                            maxDaysAfterStart={MAX_DATE_RANGE}
                            error={errors.dateRange}
                            onSearch={onDateChange}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <StaticField
                            id="serviceId"
                            label="Service"
                            customClass="inputStyle"
                            content={serviceDetailsById[state?.serviceId]?.name || ''}
                        />
                        <SingleSelect
                            id="teamId"
                            label="Team"
                            placeholder="Select a team..."
                            disabled={!hasRole(acceptedRoles, roles) || !filters?.serviceId}
                            mandatory={true}
                            menuItems={teamsForService || []}
                            selectedId={filters.teamId || ''}
                            selected={getObjFromId(teamsForService, filters?.teamId)}
                            error={errors.teamId}
                            onChange={(chosenId) => onUpdate('teamId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="typeIds"
                            label="Appointment Type"
                            placeholder="Select appointment type..."
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles) || !filters?.serviceId}
                            customClass="standardAbsolute"
                            menuItems={appointmentTypes}
                            selectedId={filters.typeIds[0] || ''}
                            selected={getObjFromId(appointmentTypes, filters?.typeIds[0])}
                            error={errors.typeIds}
                            onChange={(chosenId) => onUpdate('typeIds', [chosenId])}
                        />
                        <RadioButtons
                            id="includeFilled"
                            label="Show Filled Appointment Slots"
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={filters.includeFilled ?? false}
                            onChange={(option) => onUpdate('includeFilled', option)}
                        />
                    </div>
                </div>
                <Button
                    id="appointmentSlotSearchButton"
                    type="button"
                    content="Update"
                    disabled={isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                    onClick={onSearch}
                />
            </form>
            <div className={local.tableWrapper}>
                <AppointmentSlotsTableManagement
                    acceptedRoles={acceptedRoles}
                    filters={filters}
                    participantId={state?.participantId || ''}
                />
            </div>
        </div>
    );
};

export default AppointmentSlotsSearch;
